import { useEffect, useMemo, useState, useCallback } from "react";
import JobLink from "@/components/JobLink";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

const STAGES = ["Planning", "Printing", "Inspection", "Slitting", "Packing"];

export default function JobsInHandReport() {
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [q, setQ] = useState("");
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = {};
    if (customerFilter !== "all") params.customer_name = customerFilter;
    if (stageFilter !== "all") params.stage = stageFilter;
    if (typeFilter !== "all") params.job_type = typeFilter;
    api.get("/reports/jobs-in-hand", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerFilter, stageFilter, typeFilter]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (urgentOnly) list = list.filter((r) => r.urgent);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) =>
        (r.customer_name || "").toLowerCase().includes(n) ||
        (r.bpr_no || "").toLowerCase().includes(n) ||
        (r.po_no || "").toLowerCase().includes(n) ||
        (r.product_code || "").toLowerCase().includes(n) ||
        (r.description || "").toLowerCase().includes(n) ||
        (r.location || "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [data, q, urgentOnly]);

  const totals = data?.totals || {};
  const byStage = totals.by_stage || {};

  const exportCSV = () => downloadCSV(
    "all-jobs-in-hand-priority-wise",
    ["Stage", "No. of days", "BPR Date", "BPR No.", "Customer Name", "PO No.", "PO Date", "Order Received Date", "Product Code", "Description", "Qty", "New/Repeat", "Location", "Remarks"],
    rows.map((r) => [
      r.stage, r.no_of_days, r.bpr_date, r.bpr_no, r.customer_name,
      r.po_no, r.po_date, r.order_received_date, r.product_code,
      r.description, r.qty, r.new_repeat, r.location, r.remarks,
    ])
  );

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Customer</label>
          <div className="flex items-center gap-1">
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="prio-customer" className="rounded-sm w-64"><SelectValue placeholder="All customers" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <button data-testid="prio-clear-customer" onClick={() => setCustomerFilter("all")}
                className="p-1.5 hover:bg-neutral-100 rounded-sm" title="Clear"><X size={14} /></button>
            )}
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">Stage</label>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger data-testid="prio-stage" className="rounded-sm w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">Type</label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="prio-type" className="rounded-sm w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="New">New</SelectItem>
              <SelectItem value="Repeat">Repeat</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 border border-neutral-200 rounded-sm px-3 py-1.5 cursor-pointer bg-white hover:bg-neutral-50">
          <input
            data-testid="prio-urgent-only"
            type="checkbox"
            checked={urgentOnly}
            onChange={(e) => setUrgentOnly(e.target.checked)}
            className="accent-[#FF5A00]"
          />
          <span className="label-caps text-[10px]">Urgent only</span>
        </label>
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search customer / BPR / PO / product</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="prio-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="e.g. Gland, MPWO-2560, LBL" />
          </div>
        </div>
        <Button data-testid="prio-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Refresh"}
        </Button>
        <Button data-testid="prio-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        <Kpi label="Total jobs in hand" value={inr(totals.jobs || 0)} />
        <Kpi label="Urgent" value={inr(totals.urgent || 0)} tone="danger" />
        <Kpi label="Total qty (labels)" value={inr(totals.total_qty || 0)} />
        {STAGES.slice(0, 4).map((s) => (
          <Kpi key={s} label={s} value={inr(byStage[s] || 0)} />
        ))}
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="prio-table">
          <thead className="bg-neutral-50 label-caps sticky top-0">
            <tr>
              <th className="text-left px-3 py-2.5">Stage</th>
              <th className="text-right px-3 py-2.5">Days</th>
              <th className="text-left px-3 py-2.5">BPR Date</th>
              <th className="text-left px-3 py-2.5">BPR No.</th>
              <th className="text-left px-3 py-2.5">Customer Name</th>
              <th className="text-left px-3 py-2.5">PO No.</th>
              <th className="text-left px-3 py-2.5">PO Date</th>
              <th className="text-left px-3 py-2.5">Order Received</th>
              <th className="text-left px-3 py-2.5">Product Code</th>
              <th className="text-left px-3 py-2.5">Description</th>
              <th className="text-right px-3 py-2.5">Qty</th>
              <th className="text-left px-3 py-2.5">New/Repeat</th>
              <th className="text-left px-3 py-2.5">Location</th>
              <th className="text-left px-3 py-2.5 min-w-64">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={14} msg="No pending jobs for this filter." />}
            {rows.slice(0, 800).map((r) => {
              const daysTone =
                r.no_of_days == null ? "text-neutral-400" :
                r.no_of_days > 30 ? "text-rose-700 font-bold" :
                r.no_of_days > 15 ? "text-amber-700 font-semibold" :
                "text-neutral-700";
              const stageColor = {
                Planning: "bg-blue-100 text-blue-800",
                Printing: "bg-indigo-100 text-indigo-800",
                Inspection: "bg-purple-100 text-purple-800",
                Slitting: "bg-teal-100 text-teal-800",
                Packing: "bg-emerald-100 text-emerald-800",
              }[r.stage] || "bg-neutral-100 text-neutral-800";
              return (
                <tr key={r.job_id}
                  className={`border-t border-neutral-100 data-row ${r.urgent ? "bg-rose-50/60" : ""}`}
                  data-testid={`prio-row-${r.job_id}`}>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold ${stageColor}`}>{r.stage}</span>
                    {r.urgent && <div className="text-[9px] uppercase tracking-widest font-bold text-rose-700 mt-0.5">Urgent</div>}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono-num ${daysTone}`}>{r.no_of_days ?? "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{(r.bpr_date || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs"><JobLink id={r.job_id} number={r.bpr_no} tab="production_flow" /></td>
                  <td className="px-3 py-2 font-medium">{r.customer_name}</td>
                  <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{r.po_no || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{(r.po_date || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{(r.order_received_date || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs">{r.product_code || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.description || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(r.qty)}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.new_repeat && (
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${
                        r.new_repeat === "New" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                        r.new_repeat === "Repeat" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                        "bg-neutral-100 text-neutral-600"
                      }`}>{r.new_repeat}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.location || "—"}</td>
                  <td className="px-3 py-2 text-xs text-neutral-600 max-w-lg whitespace-pre-wrap">{r.remarks || "—"}</td>
                </tr>
              );
            })}
            {rows.length > 800 && (
              <tr><td colSpan={14} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 800 of {rows.length} rows. Export CSV for full list.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        Priority = Urgent flag first, then oldest jobs first (highest No. of days). Rows &gt;30 days shown in red, 16-30 days in amber.
      </p>
    </div>
  );
}



